import { cloudApi, signedPartUrl } from './api';
import { getCloudConfig } from './config';

function uploadId() {
  const random = window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2, 10);
  return `upload-${Date.now()}-${random}`;
}

function fileExtension(name = '') {
  return String(name).toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] || '';
}

const PANO_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'avif', 'bmp']);
const PANO_CONTENT_TYPES = Object.freeze({
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  avif: 'image/avif',
  bmp: 'image/bmp',
});
const AUDIO_CONTENT_TYPES = Object.freeze({
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  ogg: 'audio/ogg',
  opus: 'audio/ogg',
  webm: 'audio/webm',
});
const RESUME_KEY_PREFIX = 'vr-tour-cloud-upload-v1';

export function supportsFile(file, kind) {
  if (kind === 'audio') {
    return String(file?.type || '').startsWith('audio/') || Boolean(AUDIO_CONTENT_TYPES[fileExtension(file?.name)]);
  }
  return String(file?.type || '').startsWith('image/') || PANO_EXTENSIONS.has(fileExtension(file?.name));
}

function contentTypeFor(file, kind) {
  const browserType = String(file?.type || '').toLowerCase();
  if (kind === 'audio') return browserType.startsWith('audio/') ? browserType : AUDIO_CONTENT_TYPES[fileExtension(file?.name)] || '';
  return browserType.startsWith('image/') ? browserType : PANO_CONTENT_TYPES[fileExtension(file?.name)] || '';
}

function resumeKey(projectId, kind, file) {
  return [RESUME_KEY_PREFIX, projectId, kind, file.name, file.size, file.lastModified || 0].join(':');
}

function readResumeSession(key) {
  try {
    const stored = window.localStorage.getItem(key);
    const value = stored ? JSON.parse(stored) : null;
    return value && typeof value.assetId === 'string' ? value : null;
  } catch {
    return null;
  }
}

function writeResumeSession(key, session) {
  try {
    window.localStorage.setItem(key, JSON.stringify(session));
  } catch {
    // Private browsing or a full quota should not stop a normal upload.
  }
}

function clearResumeSession(key) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Nothing to clean up when storage is unavailable.
  }
}

function canResume(session, file, kind) {
  const asset = session?.asset || {};
  return session?.state === 'open'
    && asset.kind === kind
    && asset.originalName === file.name
    && Number(asset.expectedBytes) === file.size;
}

function uploadedPartNumbers(session) {
  const parts = session?.uploadedParts || session?.uploaded_parts || [];
  return new Set(
    (Array.isArray(parts) ? parts : []).map((part) => Number(part?.number || part?.partNumber || part)).filter(Number.isFinite)
  );
}

async function uploadFile({ projectId, item, kind, onProgress, onAsset, signal }) {
  const { file, clientId = uploadId() } = item;
  if (!supportsFile(file, kind)) {
    throw new Error(kind === 'pano' ? '请选择受支持的图片文件' : '请选择音频文件');
  }

  const contentType = contentTypeFor(file, kind);
  if (!contentType) {
    throw new Error(kind === 'pano' ? '无法识别图片格式' : '无法识别音频格式');
  }

  onProgress?.({ clientId, name: file.name, kind, state: 'creating', loaded: 0, total: file.size });
  const storageKey = resumeKey(projectId, kind, file);
  const resume = readResumeSession(storageKey);
  let assetId = resume?.assetId || null;
  let session = null;

  if (assetId) {
    try {
      const existing = await cloudApi.getUpload(assetId);
      if (canResume(existing, file, kind)) {
        session = existing;
        onProgress?.({ clientId, name: file.name, kind, state: 'resuming', loaded: 0, total: file.size });
      } else {
        clearResumeSession(storageKey);
        assetId = null;
      }
    } catch (error) {
      if (error?.status === 404 || error?.status === 409 || error?.status === 410) {
        clearResumeSession(storageKey);
        assetId = null;
      } else {
        throw error;
      }
    }
  }

  if (!assetId) {
    const created = await cloudApi.createUpload(projectId, {
      kind,
      name: file.name,
      contentType,
      size: file.size,
    });
    assetId = created.assetId || created.asset?.id || created.id;
    if (!assetId) throw new Error('服务端没有返回素材 ID');
    writeResumeSession(storageKey, { assetId, createdAt: Date.now() });
  }

  session = session || await cloudApi.getUpload(assetId);
  const partSize = Number(session?.partSize || session?.part_size || getCloudConfig().uploadPartBytes);
  const partCount = Number(session?.partCount || session?.part_count || Math.ceil(file.size / partSize));
  if (!Number.isFinite(partSize) || partSize <= 0 || !Number.isFinite(partCount) || partCount < 1) {
    throw new Error('服务端上传会话无效');
  }

  const completed = uploadedPartNumbers(session);
  let loaded = 0;
  for (let partNumber = 1; partNumber <= partCount; partNumber += 1) {
    const start = (partNumber - 1) * partSize;
    const end = Math.min(file.size, start + partSize);
    if (completed.has(partNumber)) {
      loaded += Math.max(0, end - start);
      continue;
    }

    onProgress?.({ clientId, name: file.name, kind, state: 'uploading', loaded, total: file.size });
    const signed = await cloudApi.signParts(assetId, [partNumber]);
    const part = file.slice(start, end);
    await cloudApi.uploadPart(signedPartUrl(signed, partNumber, assetId), part, signal);
    loaded += part.size;
    onProgress?.({ clientId, name: file.name, kind, state: 'uploading', loaded, total: file.size });
  }

  onProgress?.({ clientId, name: file.name, kind, state: 'finalizing', loaded: file.size, total: file.size });
  const completion = await cloudApi.completeUpload(assetId);
  const asset = completion?.asset || completion;
  clearResumeSession(storageKey);
  onProgress?.({ clientId, name: file.name, kind, state: asset?.state || 'queued', loaded: file.size, total: file.size });
  const result = {
    clientId,
    fileName: file.name,
    asset: asset || { id: assetId, state: 'queued' },
    project: completion?.project ? completion : null,
  };
  await onAsset?.(result);
  return result;
}

export async function uploadFiles({ projectId, files, kind, onProgress, onAsset, signal }) {
  const queue = files.map((file, index) => ({ file, clientId: `${uploadId()}-${index}` }));
  const results = new Array(queue.length);
  const concurrency = Math.min(getCloudConfig().uploadConcurrency, queue.length);
  let nextIndex = 0;

  async function worker() {
    while (true) {
      const current = nextIndex;
      nextIndex += 1;
      if (current >= queue.length) return;
      const item = queue[current];
      try {
        results[current] = await uploadFile({ projectId, item, kind, onProgress, onAsset, signal });
      } catch (error) {
        onProgress?.({
          clientId: item.clientId,
          name: item.file.name,
          kind,
          state: 'failed',
          loaded: 0,
          total: item.file.size,
          error: error.message || '上传失败',
        });
        results[current] = { clientId: item.clientId, fileName: item.file.name, error };
      }
    }
  }

  await Promise.all(Array.from({ length: Math.max(1, concurrency) }, () => worker()));
  return results;
}
